import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  ApiError,
  LoadedHomePage,
  LoadedCmsPage,
  LoadPage,
  LoadPrivacyAndCookies,
  LoadPrivacyAndCookiesSuccess,
  LoadSearchPageSuccess,
  LoadContactPageSuccess,
  LoadGeneralDataSuccess,
  SetBreadcrumbs,
  SetRole,
  ExportGuidelines,
} from '../actions';
import { catchError, firstValueFrom, from, map, of, switchMap, take, tap, withLatestFrom } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { GeneralState } from '../general.reducer';
import { CmsService } from '../../services/cms.service';
import { AppLanguage, Breadcrumbs, CmsPage, HomePage, PageBase } from '../../models';
import {
  HOME_BREADCRUMB,
  selectCMSPage,
  selectLanguage,
  selectGeneralDataForBreadcrumbs,
  selectActivePage,
} from '../selectors';
import { LocalizationState } from '@ca/ca-ng-core';

@Injectable()
export class PagesEffects {
  private actions$ = inject(Actions);
  private store = inject<
    Store<{
      general: GeneralState;
      breadcrumbs: Breadcrumbs;
      localization: LocalizationState<AppLanguage>;
    }>
  >(Store);
  private cms = inject(CmsService);

  onFetch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPage),
      withLatestFrom(this.store.pipe(select(selectLanguage))),
      switchMap(([actionData, language]) =>
        this.store.select(selectCMSPage(actionData.path, language)).pipe(
          take(1), // Only take the latest value once
          switchMap((existingPage) => {
            const request = { ...actionData, language };
            if (existingPage) {
              return of(
                actionData.path.length === 0
                  ? LoadedHomePage({ request, page: existingPage as HomePage })
                  : LoadedCmsPage({ request, page: existingPage as CmsPage }),
              );
            } else {
              return from(this.cms.getPage(request)).pipe(
                map((page) =>
                  actionData.path.length === 0
                    ? LoadedHomePage({ request, page: page as HomePage })
                    : LoadedCmsPage({ request, page: page as CmsPage }),
                ),
                catchError((error) => of(ApiError({ error }))),
              );
            }
          }),
        ),
      ),
    ),
  );

  pageLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LoadedHomePage,
          LoadedCmsPage,
          LoadSearchPageSuccess,
          LoadContactPageSuccess,
          LoadPrivacyAndCookiesSuccess,
        ),
        withLatestFrom(this.store.pipe(select(selectGeneralDataForBreadcrumbs))),
        tap(([{ page }, general]) => {
          const breadcrumbs = buildBreadcrumbs(page, general);
          this.store.dispatch(SetBreadcrumbs({ breadcrumbs }));
        }),
      ),
    { dispatch: false },
  );

  onRoleChanged$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SetRole),
        withLatestFrom(
          this.store.pipe(select(selectGeneralDataForBreadcrumbs)),
          this.store.pipe(select(selectActivePage)),
        ),
        tap(([{ role }, general, activePage]) => {
          const breadcrumbs = buildBreadcrumbs(activePage, { ...general, role });
          this.store.dispatch(SetBreadcrumbs({ breadcrumbs }));
        }),
      ),
    {
      dispatch: false,
    },
  );

  generalDataLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoadGeneralDataSuccess),
        withLatestFrom(
          this.store.pipe(select(selectGeneralDataForBreadcrumbs)),
          this.store.pipe(select(selectActivePage)),
        ),
        tap(([_, general, activePage]) => {
          if (!activePage) {
            return;
          }
          const breadcrumbs = buildBreadcrumbs(activePage, general);
          this.store.dispatch(SetBreadcrumbs({ breadcrumbs }));
        }),
      ),
    { dispatch: false },
  );

  onLoadSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadPrivacyAndCookies),
      withLatestFrom(this.store.pipe(select(selectLanguage))),
      switchMap(([action, language]) =>
        this.cms.getOrphan(language, action.metaUrl).pipe(
          map((page) => LoadPrivacyAndCookiesSuccess({ page })),
          catchError((error) => of(ApiError({ error }))),
        ),
      ),
    ),
  );

  onExportGuidelines$ = createEffect(() => this.actions$.pipe(
    ofType(ExportGuidelines),
    withLatestFrom(this.store.pipe(select(selectLanguage))),
    tap(([guidelines, language]) => firstValueFrom(this.cms.exportGuidelines(language, guidelines)).then(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'guidelines.pdf';
      a.click();
      URL.revokeObjectURL(url);
    }
    )),
  ),
    {
      dispatch: false
    });
}

interface GeneralBreadcrumbData {
  role: GeneralState['role'];
  roles: GeneralState['navigationFilters'];
  language: AppLanguage;
  home: string;
}

function buildBreadcrumbs(page: PageBase | null, general: GeneralBreadcrumbData): Breadcrumbs {
  const { role, roles, language, home } = general;
  const breadcrumbs: Breadcrumbs = [];

  if (!page) {
    return breadcrumbs;
  }

  const roleSegment = roles.find((r) => r.filter === role);
  const hasPageBreadcrumbs = (page.breadcrumbs?.length ?? 0) > 0;

  if (!roleSegment && !hasPageBreadcrumbs) {
    return breadcrumbs;
  }

  const HOME = HOME_BREADCRUMB[language];
  breadcrumbs.push({ ...HOME, title: home });

  if (roleSegment) {
    breadcrumbs.push({
      metaUrl: HOME.metaUrl,
      routerLink: [],
      title: roleSegment.name,
    });
  }

  if (hasPageBreadcrumbs) {
    const routerLinkSegments = page.breadcrumbs.map((s) => s.segments[language]);
    const others = page.breadcrumbs.map((bc, index) => ({
      routerLink: routerLinkSegments.filter((_, i) => i <= index),
      metaUrl: bc.segments[language][bc.segments[language].length - 1],
      title: bc.title,
    }));
    breadcrumbs.push(...others);
  }

  return breadcrumbs;
}
